// src/Pages/Dashboard/widgets/ProjectView/Tabs/Reports/ExpenseReportPreview.js

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosWithAuth from "../../../../../../Redux/UtilsRedux/axiosWithAuth";
import html2pdf from "html2pdf.js";
import { API_ROUTES } from "../../../../../../Redux/Constants/apiRoutes";
import "../../../../../../Stylesheets/Dashboard/Tabs/Reports/ExpenseReportPrint.css";
import site360Logo from "../../../../../../assets/images/logo.png";
import { FaChevronLeft } from "react-icons/fa";

// ✅ adjust path if your modal lives elsewhere
import ViewReportModal from "../Expense/Component/ViewReportModal";

const formatCurrency = (val) => {
  if (val == null || Number.isNaN(Number(val))) return "₹0";
  return "₹" + Number(val).toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatDateTime = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ExpenseReportPreview = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const queryParams = useMemo(
    () => ({
      type: qs.get("type") || "expenses",
      projectId: qs.get("projectId"),
      from: qs.get("from"),
      to: qs.get("to"),
      detail: qs.get("detail") || "summary",
      category: qs.get("category") || "all",
    }),
    [qs]
  );

  const isDetailed = queryParams.detail === "detailed";

  // ------------------- Fetch JSON for preview -------------------
  useEffect(() => {
    const fetchReport = async () => {
      try {
        if (!queryParams.projectId || !queryParams.from || !queryParams.to) {
          setError("Missing projectId / from / to");
          setLoading(false);
          return;
        }

        const params = new URLSearchParams({
          type: queryParams.type,
          projectId: queryParams.projectId,
          from: queryParams.from,
          to: queryParams.to,
          detail: queryParams.detail,
          category: queryParams.category,
          format: "json",
        }).toString();

        const url = `${API_ROUTES.GENERATE_REPORT}?${params}`;
        const res = await axiosWithAuth.get(url);

        setReport(res.data.data);
        setError(null);
      } catch (err) {
        console.error("Report fetch failed", err);
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load report data";
        setError(msg);
        setReport(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [queryParams]);

  // ------------------- Download PDF -------------------
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    if (!queryParams.projectId || !queryParams.from || !queryParams.to) return;

    const container = document.getElementById("expense-report-doc");
    if (!container) {
      console.error("Report container not found");
      return;
    }

    try {
      setDownloading(true);

      const filename = `expense-report-${queryParams.from}-to-${queryParams.to}.pdf`;

      const opt = {
        margin: [10, 10, 10, 10],
        filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      };

      await html2pdf().set(opt).from(container).save();
    } catch (e) {
      console.error("PDF download failed", e);
    } finally {
      setDownloading(false);
    }
  };

  // ------------------- Back to Expenses -------------------
  const handleBackToExpenses = () => {
    if (location.state?.returnTo) {
      navigate(location.state.returnTo);
      return;
    }
    navigate(-1);
  };

  // ------------------- Filters modal state -------------------
  const [showFilters, setShowFilters] = useState(false);

  const [tmpFrom, setTmpFrom] = useState(queryParams.from || "");
  const [tmpTo, setTmpTo] = useState(queryParams.to || "");
  const [tmpCategory, setTmpCategory] = useState(queryParams.category || "all");
  const [tmpDetail, setTmpDetail] = useState(queryParams.detail || "summary");

  // when modal opens, sync tmp state with current URL
  useEffect(() => {
    if (!showFilters) return;
    setTmpFrom(queryParams.from || "");
    setTmpTo(queryParams.to || "");
    setTmpCategory(queryParams.category || "all");
    setTmpDetail(queryParams.detail || "summary");
  }, [showFilters, queryParams]);

  const handleApplyFilters = () => {
    const next = new URLSearchParams(location.search);

    next.set("from", tmpFrom);
    next.set("to", tmpTo);
    next.set("detail", tmpDetail);
    next.set("category", tmpCategory);
    next.set("type", "expenses"); // ✅ keep type stable

    setLoading(true);
    setError(null);

    navigate({ search: next.toString() }, { replace: false });

    setShowFilters(false);
  };

  // ------------------- Loading/Error views -------------------
  if (loading) {
    return (
      <div className="report-print-root">
        <div className="report-preview-scroll">
          <div className="report-print-shell">
            <div className="report-loading">Preparing report…</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="report-print-root">
        <div className="report-preview-scroll">
          <div className="report-print-shell">
            <div className="report-error">
              <h2>Unable to generate report</h2>
              <p>{error || "No data available for the selected filters."}</p>
              <button
                className="report-filter-btn"
                onClick={() => setShowFilters(true)}
              >
                Edit Filters
              </button>
              <button
                className="report-back-btn"
                style={{ marginTop: 8 }}
                onClick={handleBackToExpenses}
              >
                <FaChevronLeft size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Filters modal */}
        <ViewReportModal
          show={showFilters}
          onClose={() => setShowFilters(false)}
          startDate={tmpFrom}
          endDate={tmpTo}
          setStartDate={setTmpFrom}
          setEndDate={setTmpTo}
          category={tmpCategory}
          setCategory={setTmpCategory}
          format={tmpDetail}
          setFormat={setTmpDetail}
          mode="filters"
          onApply={handleApplyFilters}
          applyLabel="Apply Filters"
        />
      </div>
    );
  }

  const { meta = {}, summary = {}, tables = [] } = report;

  const dailyTable =
    tables.find((t) => /daily/i.test(t.title || "")) || tables[0] || null;
  const categoryTable =
    tables.find((t) => /category/i.test(t.title || "")) || tables[1] || null;
  const topTable =
    tables.find((t) => /top/i.test(t.title || "")) || tables[2] || null;

  let topCategoryName = null;
  let topCategoryAmount = 0;

  Object.entries(summary).forEach(([k, v]) => {
    const m = k.match(/^category_(.+)_amount$/);
    if (!m) return;
    const name = m[1].replace(/_/g, " ");
    const amount = Number(v) || 0;
    if (amount > topCategoryAmount) {
      topCategoryAmount = amount;
      topCategoryName = name;
    }
  });

  if (!topCategoryName && categoryTable && Array.isArray(categoryTable.rows)) {
    const row = categoryTable.rows[0];
    if (row) {
      topCategoryName = row[0];
      topCategoryAmount = Number(row[2]) || 0;
    }
  }

  return (
    <div className="report-print-root">
      {/* Top toolbar */}
      <div className="report-toolbar">
        <div className="report-toolbar-left">
          <button
            className="report-back-btn"
            onClick={handleBackToExpenses}
            aria-label="Back to Expenses"
          >
            <FaChevronLeft size={14} />
          </button>

          <div className="report-toolbar-title">
            <h2>Expense Report Preview</h2>
            <span className="report-toolbar-period">
              {queryParams.from} → {queryParams.to}
            </span>
          </div>
        </div>

        <div className="report-toolbar-right">
          <button
            className="report-filter-btn"
            onClick={() => setShowFilters(true)}
          >
            Edit Filters
          </button>

          <button
            className="report-download-btn"
            onClick={handleDownloadPdf}
            disabled={downloading}
          >
            {downloading ? "Generating PDF…" : "Download PDF"}
          </button>
        </div>
      </div>

      {/* Screen watermark (optional visual) */}
      <div className="report-watermark">Site360</div>

      {/* Scrollable preview */}
      <div className="report-preview-scroll">
        <div id="expense-report-doc" className="report-print-shell">
          {/* Watermark INSIDE PDF capture */}
          <img
            src={site360Logo}
            alt=""
            className="report-doc-watermark"
            crossOrigin="anonymous"
          />

          {/* HEADER */}
          <header className="report-header">
            <div className="report-header-left">
              <div className="report-logo">Site360</div>
              <div className="report-title-block">
                <h1>Expense Report</h1>
                {meta.projectName && (
                  <p className="report-project">{meta.projectName}</p>
                )}
              </div>
            </div>

            <div className="report-header-right">
              <div className="report-meta-row">
                <span className="label">Type</span>
                <span className="value">
                  {(meta.type || "expenses").replace(/^./, (c) =>
                    c.toUpperCase()
                  )}
                </span>
              </div>

              <div className="report-meta-row">
                <span className="label">Period</span>
                <span className="value">
                  {(meta.from || queryParams.from || "—")} →{" "}
                  {(meta.to || queryParams.to || "—")}
                </span>
              </div>

              <div className="report-meta-row">
                <span className="label">Detail Level</span>
                <span className="value">
                  {isDetailed ? "Detailed" : "Summary"}
                </span>
              </div>

              <div className="report-meta-row">
                <span className="label">Generated</span>
                <span className="value">{formatDateTime(meta.generatedAt)}</span>
              </div>
            </div>
          </header>

          {/* SUMMARY */}
          <section className="report-section">
            <div className="section-header">
              <h2>Summary</h2>
              <p>
                {isDetailed
                  ? "Detailed breakdown of project expenses in this period."
                  : "High-level snapshot of project expenses in this period."}
              </p>
            </div>

            <div className="metric-grid">
              <div className="metric-card">
                <div className="metric-label">Total Expenses</div>
                <div className="metric-value">
                  {formatCurrency(summary.totalExpenses)}
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-label">Total Entries</div>
                <div className="metric-value">{summary.totalEntries ?? 0}</div>
              </div>

              <div className="metric-card">
                <div className="metric-label">Pending Bills</div>
                <div className="metric-value">{summary.pendingBill ?? 0}</div>
                {summary.withBill != null && (
                  <div className="metric-helper">
                    With Bill: {summary.withBill}
                  </div>
                )}
              </div>
            </div>

            {topCategoryName && (
              <div className="top-category-banner">
                <span className="tag">Top Category</span>
                <span className="name">{topCategoryName}</span>
                <span className="amount">
                  {formatCurrency(topCategoryAmount)}
                </span>
              </div>
            )}
          </section>

          {/* DAILY SUMMARY */}
          {dailyTable && (
            <section className="report-section">
              <div className="section-header">
                <h3>{dailyTable.title || "Daily Expense Summary"}</h3>
              </div>

              <table className="report-table">
                <thead>
                  <tr>
                    {dailyTable.columns.map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dailyTable.rows.map((row, idx) => (
                    <tr key={idx}>
                      {row.map((cell, i) => (
                        <td
                          key={i}
                          className={i === row.length - 1 ? "right" : ""}
                        >
                          {i === 0 ? formatDate(cell) : cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* CATEGORY BREAKDOWN */}
          {categoryTable && (
            <section className="report-section">
              <div className="section-header">
                <h3>{categoryTable.title || "Category Breakdown"}</h3>
              </div>

              <table className="report-table">
                <thead>
                  <tr>
                    {categoryTable.columns.map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {categoryTable.rows.map((row, idx) => (
                    <tr key={idx}>
                      {row.map((cell, i) => (
                        <td
                          key={i}
                          className={i === row.length - 1 ? "right" : ""}
                        >
                          {i === row.length - 1
                            ? formatCurrency(cell)
                            : cell || "-"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* TOP EXPENSES */}
          {topTable && (
            <section className="report-section">
              <div className="section-header">
                <h3>{topTable.title || "Top Expenses"}</h3>
                <p>Highest-value expenses within the selected range.</p>
              </div>

              <table className="report-table">
                <thead>
                  <tr>
                    {topTable.columns.map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topTable.rows.map((row, idx) => (
                    <tr key={idx}>
                      {row.map((cell, i) => (
                        <td key={i} className={i === 3 ? "right" : ""}>
                          {i === 0 ? formatDate(cell) : cell || "-"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* FOOTER */}
          <footer className="report-footer">
            <div className="footer-brand">Site360</div>
            <div className="footer-text">
              Construction Management Platform · https://site360.app
            </div>
          </footer>
        </div>
      </div>

      {/* Bottom sticky bar */}
      <div className="report-bottom-sticky">
        <div className="bottom-meta">
          <div className="title">Expense Report</div>
          <div className="period">
            {queryParams.from} → {queryParams.to}
          </div>
        </div>

        <button
          className="report-download-btn"
          onClick={handleDownloadPdf}
          disabled={downloading}
        >
          {downloading ? "Generating PDF…" : "Download PDF"}
        </button>
      </div>

      {/* Filters modal */}
      <ViewReportModal
        show={showFilters}
        onClose={() => setShowFilters(false)}
        startDate={tmpFrom}
        endDate={tmpTo}
        setStartDate={setTmpFrom}
        setEndDate={setTmpTo}
        category={tmpCategory}
        setCategory={setTmpCategory}
        format={tmpDetail}
        setFormat={setTmpDetail}
        mode="filters"
        onApply={handleApplyFilters}
        applyLabel="Apply Filters"
      />
    </div>
  );
};

export default ExpenseReportPreview;
